import React, { useState, useCallback, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { apiService } from '../../services';
import { ClaimsUploadResult } from '../../types/claims';

interface ClaimsDropZoneProps {
    children: React.ReactNode;
    onUploadComplete: (result: ClaimsUploadResult) => void;
    onUploadError?: (error: string) => void;
}

const EDI_EXTENSIONS = ['.edi', '.dat', '.x12', '.835', '.837', '.837i', '.837p', '.837d', '.837cln', '.277'];
const EDI_ACCEPT_DISPLAY = '.835, .837, .837i, .837p, .837d, .edi, .x12, .277';

const isEdiFile = (fileName: string): boolean => {
    const lowerName = fileName.toLowerCase();
    return EDI_EXTENSIONS.some(ext => lowerName.endsWith(ext));
};

// MIME types that are definitely NOT EDI (used for drag-time classification)
const NON_EDI_MIME_TYPES = new Set([
    'application/pdf', 'application/json', 'text/plain', 'text/csv',
    'application/msword', 'application/xml',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const NON_EDI_MIME_PREFIXES = ['image/'];

const isNonEdiMime = (type: string): boolean => {
    if (!type) return false;
    const lower = type.toLowerCase();
    if (NON_EDI_MIME_TYPES.has(lower)) return true;
    return NON_EDI_MIME_PREFIXES.some(prefix => lower.startsWith(prefix));
};

type DragStatus = 'idle' | 'valid' | 'invalid';

const REJECTION_DISPLAY_MS = 3000;

const ClaimsDropZone: React.FC<ClaimsDropZoneProps> = ({
    children,
    onUploadComplete,
    onUploadError
}) => {
    const [dragStatus, setDragStatus] = useState<DragStatus>('idle');
    const [rejectedNames, setRejectedNames] = useState<string[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const rejectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearRejection = useCallback(() => {
        if (rejectionTimerRef.current) clearTimeout(rejectionTimerRef.current);
        rejectionTimerRef.current = null;
        setDragStatus('idle');
        setRejectedNames([]);
    }, []);

    const classifyDragItems = useCallback((dataTransfer: DataTransfer): DragStatus => {
        const items = Array.from(dataTransfer.items);
        const fileItems = items.filter(item => item.kind === 'file');
        if (fileItems.length === 0) return 'valid';

        const hasAnyNonEdi = fileItems.some(item => isNonEdiMime(item.type));
        return hasAnyNonEdi ? 'invalid' : 'valid';
    }, []);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types.includes('Files')) {
            clearRejection();
            const status = classifyDragItems(e.dataTransfer);
            setDragStatus(status);
            e.dataTransfer.dropEffect = status === 'invalid' ? 'none' : 'copy';
        }
    }, [classifyDragItems, clearRejection]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types.includes('Files')) {
            const status = classifyDragItems(e.dataTransfer);
            setDragStatus(status);
            e.dataTransfer.dropEffect = status === 'invalid' ? 'none' : 'copy';
        }
    }, [classifyDragItems]);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const currentTarget = e.currentTarget as HTMLElement;
        const relatedTarget = e.relatedTarget as Node | null;
        // relatedTarget is null when the drag leaves the document entirely (e.g. dragged
        // out through the top of the browser into the tab/address bar), where clientX/Y
        // get clamped to viewport bounds instead of going negative, making a coordinate
        // check unreliable. It's a descendant of currentTarget when we just moved onto a
        // child element, which should not clear the overlay.
        if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
            setDragStatus('idle');
        }
    }, []);

    const handleDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length === 0) {
            setDragStatus('idle');
            return;
        }

        // Filter to only EDI files
        const ediFiles = droppedFiles.filter(file => isEdiFile(file.name));
        const invalidFiles = droppedFiles.filter(file => !isEdiFile(file.name));

        if (invalidFiles.length > 0) {
            setRejectedNames(invalidFiles.map(f => f.name));
            setDragStatus('invalid');
            rejectionTimerRef.current = setTimeout(clearRejection, REJECTION_DISPLAY_MS);
        } else {
            setDragStatus('idle');
        }

        if (ediFiles.length === 0) return;

        setIsUploading(true);
        try {
            const results = await apiService.uploadClaimsEdi(ediFiles);
            
            // Aggregate results from all batches
            const aggregatedResult: ClaimsUploadResult = {
                files_processed: results.reduce((sum, r) => sum + r.files_processed, 0),
                claims_created: results.reduce((sum, r) => sum + r.claims_created, 0),
                claims_updated: results.reduce((sum, r) => sum + r.claims_updated, 0),
                duplicates: results.flatMap(r => r.duplicates),
                errors: results.flatMap(r => r.errors),
                invalid_pcns: results.flatMap(r => r.invalid_pcns || []),
                claims: results.flatMap(r => r.claims),
                claim_ids: results.flatMap(r => r.claim_ids),
            };

            onUploadComplete(aggregatedResult);

            // Build error messages for display
            const errorMessages: string[] = [];

            if (aggregatedResult.errors.length > 0) {
                const fileErrors = aggregatedResult.errors.map(e => `${e.file}: ${e.error}`).join(', ');
                errorMessages.push(`File errors: ${fileErrors}`);
            }

            if (aggregatedResult.invalid_pcns.length > 0) {
                const pcnErrors = aggregatedResult.invalid_pcns
                    .slice(0, 5) // Limit to first 5 to avoid very long messages
                    .map(e => `PCN "${e.pcn}"`)
                    .join(', ');
                const moreCount = aggregatedResult.invalid_pcns.length > 5
                    ? ` and ${aggregatedResult.invalid_pcns.length - 5} more`
                    : '';
                errorMessages.push(`Invalid PCN values skipped: ${pcnErrors}${moreCount}`);
            }

            if (errorMessages.length > 0) {
                onUploadError?.(errorMessages.join('. '));
            }
        } catch (error) {
            onUploadError?.(error instanceof Error ? error.message : 'Upload failed');
        } finally {
            setIsUploading(false);
        }
    }, [onUploadComplete, onUploadError]);

    return (
        <Box
            sx={{ position: 'relative', width: '100%', height: '100%' }}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {children}

            {/* Drop Zone Overlay */}
            {(dragStatus === 'valid' || dragStatus === 'invalid') && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(4px)',
                        border: '4px dashed',
                        borderRadius: 2,
                        m: 0.5,
                        ...(dragStatus === 'invalid'
                            ? { backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: 'error.main' }
                            : { backgroundColor: 'rgba(59, 130, 246, 0.1)', borderColor: 'primary.main' }),
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 4,
                            py: 3,
                            backgroundColor: 'background.paper',
                            borderRadius: 2,
                            boxShadow: 3,
                            border: '1px solid',
                            borderColor: dragStatus === 'invalid' ? 'error.light' : 'primary.light',
                        }}
                    >
                        {dragStatus === 'invalid' ? (
                            <>
                                <Box
                                    component="svg"
                                    sx={{ width: 64, height: 64, color: 'error.main' }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                    />
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="subtitle1" fontWeight={600} color="error.dark">
                                        Unsupported file type
                                    </Typography>
                                    {rejectedNames.length > 0 ? (
                                        <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                                            {rejectedNames.join(', ')} {rejectedNames.length === 1 ? 'is' : 'are'} not supported
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                                            This file type is not supported
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Accepted: {EDI_ACCEPT_DISPLAY}
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            <>
                                <Box
                                    component="svg"
                                    sx={{ width: 64, height: 64, color: 'primary.main' }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15m0-3l-3-3m0 0l-3 3m3-3V15"
                                    />
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                                        Drop EDI files here to import claims
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Supports {EDI_ACCEPT_DISPLAY}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            )}

            {/* Upload Progress Overlay */}
            {isUploading && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <CircularProgress size={48} />
                        <Typography color="text.secondary">
                            Processing EDI files...
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ClaimsDropZone;
